import CT from '@marketplace-ai/tags';
import type { ColDef } from 'ag-grid-community';
import {
  AllCommunityModule,
  colorSchemeLightCold,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef, useState } from 'react';
import { EmptyState, LoadingSpinner } from '~/components';
import { FilterBanner, useFilterBanner } from '~/components/agGrid/FilterBanner';
import {
  ACTIONS_COLUMN_BASE,
  ActionsRenderer,
  BASE_GRID_STYLE,
  type ActionDef,
} from '~/components/Table/gridUtil';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { withNewRecordPill } from '../newRecords/NewRecordPill';
import { useNewRecordPills } from '../newRecords/useNewRecords';
import { boardCertColumns } from './constants';
import { BoardCertFormValues } from './types';

ModuleRegistry.registerModules([AllCommunityModule]);

const theme = themeQuartz.withPart(colorSchemeLightCold).withParams(BASE_GRID_STYLE);

const BoardCertNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.boardCertifications}
    title="No board certifications yet"
    cyTag={CT.boardCerts.table.emptyState}
  />
);

interface GridProps {
  handleGridDelete: (id: number) => void;
  handleGridEdit: (id: number) => void;
  data: BoardCertFormValues[];
  loading: boolean;
}

export const BoardCertsGrid = (props: GridProps) => {
  const { handleGridDelete, handleGridEdit, loading, data } = props;

  const [colDefs] = useState<ColDef[]>([
    { field: 'id', hide: true },
    ...boardCertColumns(),
    {
      ...ACTIONS_COLUMN_BASE,
      width: 80,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        actions: [
          { type: 'edit', handler: handleGridEdit, cyTag: CT.boardCerts.table.editBtn },
          { type: 'delete', handler: handleGridDelete, cyTag: CT.boardCerts.table.deleteBtn },
        ] satisfies ActionDef[],
      },
    },
  ]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: '8px',
        paddingBottom: '8px',
        lineHeight: '1.5',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      filter: true,
      resizable: true,
      flex: 1,
      wrapText: false,
      autoHeight: false,
      tooltipValueGetter: (params) => {
        const formatted = params.valueFormatted;
        if (formatted != null && formatted !== '') return String(formatted);
        const raw = params.value;
        if (raw == null) return '';
        if (typeof raw === 'string' || typeof raw === 'number') return String(raw);
        return '';
      },
    };
  }, []);

  const gridRef = useRef<AgGridReact>(null);
  // [MCV-1812] Records a document parse just created carry a "New" pill for this visit.
  const newRecordIds = useNewRecordPills('board_certifications', data);
  const columnDefs = useMemo(
    () => withNewRecordPill(colDefs, newRecordIds),
    [colDefs, newRecordIds]
  );
  const { filtersActive, handleFilterChanged, clearFilters, registerGridApi } = useFilterBanner();

  return (
    <div
      className="h-full w-full p-0 overflow-auto flex flex-col"
      data-cy={CT.boardCerts.table.container}
    >
      <FilterBanner active={filtersActive} onClear={clearFilters} />
      <div className="flex-1 min-h-0">
        <AgGridReact
          theme={theme}
          colResizeDefault="shift"
          tooltipShowDelay={350}
          enableFilterHandlers={true}
          rowData={data}
          loading={loading}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loadingOverlayComponent={() => <LoadingSpinner size={12} />}
          noRowsOverlayComponent={BoardCertNoRowsOverlay}
          pagination={true}
          paginationAutoPageSize={true}
          onFilterChanged={handleFilterChanged}
          onGridReady={(params) => registerGridApi(params.api)}
          ref={gridRef}
        />
      </div>
    </div>
  );
};
